import { useState, useEffect } from 'react';
import { Mail, Building2, Users, Target, CheckCircle, XCircle, Clock, Eye, Edit, Save, Send, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface BetaApplication {
  id: string;
  created_at: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  company_name: string;
  company_type: string;
  employees_range: string;
  main_goal: string;
  message: string;
  consent_contact: boolean;
  admin_notes: string;
  updated_at: string;
  status: 'new' | 'accepted' | 'rejected' | 'contacted';
  user_id: string | null;
}

interface BetaFounder {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  phone: string;
  created_at: string;
  trial_ends_at: string;
  role: string;
}

export function BetaFoundersManagement() {
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [betaFounders, setBetaFounders] = useState<BetaFounder[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'applications' | 'founders'>('founders');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedApp, setSelectedApp] = useState<BetaApplication | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<BetaApplication>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadApplications();
    loadBetaFounders();
  }, []);

  async function loadApplications() {
    try {
      const { data, error } = await supabase
        .from('beta_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error loading beta applications:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadBetaFounders() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, company, phone, created_at, trial_ends_at, role')
        .eq('role', 'beta_founder')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBetaFounders(data || []);
    } catch (error) {
      console.error('Error loading beta founders:', error);
    }
  }

  async function updateStatus(id: string, newStatus: BetaApplication['status']) {
    try {
      // If accepting, call the edge function to create user account
      if (newStatus === 'accepted') {
        const application = applications.find(app => app.id === id);
        if (!application) {
          alert('Candidature introuvable');
          return;
        }

        if (!confirm(`Confirmer l'acceptation de ${application.firstname} ${application.lastname} ?\n\nCela va créer un compte utilisateur et envoyer un email de bienvenue.`)) {
          return;
        }

        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          alert('Session expirée. Veuillez vous reconnecter.');
          return;
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-beta-access`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: application.email,
              applicationId: application.id,
              firstName: application.firstname,
              lastName: application.lastname,
              organization: application.company_name,
            }),
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Erreur lors de la création du compte');
        }

        alert(`Compte créé avec succès pour ${application.email}\nUn email de bienvenue a été envoyé avec les identifiants.`);
        loadApplications();
      } else {
        // For other status changes, just update directly
        const { error } = await supabase
          .from('beta_applications')
          .update({ status: newStatus })
          .eq('id', id);

        if (error) throw error;
        loadApplications();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }

  async function resendWelcomeEmail(userId: string, email: string, name: string) {
    if (!confirm(`Renvoyer l'email de bienvenue à ${name} (${email}) ?\n\nUn nouveau mot de passe temporaire sera généré et envoyé.`)) {
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        alert('Session expirée. Veuillez vous reconnecter.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resend-welcome-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }

      alert(`Email de bienvenue renvoyé avec succès à ${email}\nUn nouveau mot de passe temporaire a été généré.`);
    } catch (error) {
      console.error('Error resending welcome email:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }

  async function saveApplication() {
    if (!selectedApp) return;

    setIsSaving(true);
    try {
      // Check if status is being changed to "accepted"
      const isChangingToAccepted = editForm.status === 'accepted' && selectedApp.status !== 'accepted';

      // Prevent re-acceptance if account already exists
      if (isChangingToAccepted && selectedApp.user_id) {
        alert('Un compte a déjà été créé pour ce candidat. Impossible de ré-accepter.');
        setIsSaving(false);
        return;
      }

      if (isChangingToAccepted) {
        if (!confirm(`Confirmer l'acceptation de ${editForm.firstname} ${editForm.lastname} ?\n\nCela va créer un compte utilisateur et envoyer un email de bienvenue.`)) {
          setIsSaving(false);
          return;
        }

        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          alert('Session expirée. Veuillez vous reconnecter.');
          setIsSaving(false);
          return;
        }

        // First update the application data (except status)
        const { status, ...updateData } = editForm;
        const { error: updateError } = await supabase
          .from('beta_applications')
          .update(updateData)
          .eq('id', selectedApp.id);

        if (updateError) throw updateError;

        // Then call the edge function to accept and create account
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-beta-access`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: editForm.email,
              applicationId: selectedApp.id,
              firstName: editForm.firstname,
              lastName: editForm.lastname,
              organization: editForm.company_name,
            }),
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Erreur lors de la création du compte');
        }

        alert(`Compte créé avec succès pour ${editForm.email}\nUn email de bienvenue a été envoyé avec les identifiants.`);
      } else {
        // For other changes, just update directly
        const { error } = await supabase
          .from('beta_applications')
          .update(editForm)
          .eq('id', selectedApp.id);

        if (error) throw error;
      }

      await loadApplications();
      setIsEditing(false);
      setSelectedApp(null);
      setEditForm({});
    } catch (error) {
      console.error('Error saving application:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsSaving(false);
    }
  }

  async function createBetaFounderManually() {
    if (!createForm.email || !createForm.firstName || !createForm.lastName) {
      alert('Email, prénom et nom sont obligatoires');
      return;
    }

    if (!confirm(`Créer un compte Beta Founder pour ${createForm.firstName} ${createForm.lastName} (${createForm.email}) ?\n\nUn email de bienvenue sera envoyé avec les identifiants.`)) {
      return;
    }

    setIsCreating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        alert('Session expirée. Veuillez vous reconnecter.');
        setIsCreating(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-beta-founder-manual`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createForm),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la création du compte');
      }

      alert(`Compte Beta Founder créé avec succès pour ${createForm.email}\nUn email de bienvenue a été envoyé avec les identifiants.`);

      setShowCreateModal(false);
      setCreateForm({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        phone: ''
      });

      loadApplications();
      loadBetaFounders();
    } catch (error) {
      console.error('Error creating beta founder:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsCreating(false);
    }
  }

  function openEditModal(app: BetaApplication) {
    setSelectedApp(app);
    setEditForm({
      firstname: app.firstname,
      lastname: app.lastname,
      email: app.email,
      phone: app.phone,
      company_name: app.company_name,
      company_type: app.company_type,
      employees_range: app.employees_range,
      main_goal: app.main_goal,
      message: app.message,
      admin_notes: app.admin_notes || '',
      status: app.status
    });
    setIsEditing(true);
  }

  const filteredApplications = selectedStatus === 'all'
    ? applications
    : applications.filter(app => app.status === selectedStatus);

  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'Nouveau',
      contacted: 'Contacté',
      accepted: 'Accepté',
      rejected: 'Refusé'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getCompanyTypeLabel = (type: string) => {
    const labels = {
      entreprise: 'Entreprise',
      independant: 'Indépendant',
      association: 'Association'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getMainGoalLabel = (goal: string) => {
    const labels = {
      suivre: 'Suivre la trésorerie',
      anticiper: 'Anticiper',
      securiser: 'Sécuriser',
      scenarios: 'Scénarios',
      autre: 'Autre'
    };
    return labels[goal as keyof typeof labels] || goal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Beta Founders</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Créer Beta Founder
          </button>
          <button
            onClick={() => {
              loadApplications();
              loadBetaFounders();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('founders')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'founders'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Beta Founders ({betaFounders.length})
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'applications'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Candidatures ({applications.length})
        </button>
      </div>

      {/* Beta Founders List */}
      {activeTab === 'founders' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Inscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Expiration essai
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {betaFounders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    Aucun Beta Founder
                  </td>
                </tr>
              ) : (
                betaFounders.map((founder) => {
                  const trialEndDate = founder.trial_ends_at ? new Date(founder.trial_ends_at) : null;
                  const isTrialActive = trialEndDate && trialEndDate > new Date();
                  const daysRemaining = trialEndDate
                    ? Math.ceil((trialEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : null;

                  return (
                    <tr key={founder.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {founder.first_name} {founder.last_name}
                        </div>
                        <div className="text-sm text-slate-500">{founder.email}</div>
                        {founder.phone && <div className="text-sm text-slate-500">{founder.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {founder.company || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {new Date(founder.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {trialEndDate ? new Date(trialEndDate).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isTrialActive ? (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Actif ({daysRemaining}j restants)
                          </span>
                        ) : trialEndDate ? (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Expiré
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Non défini
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <>
          {/* Statistics */}
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-slate-100 rounded-lg p-4">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-600">Total</div>
            </div>
            <div className="bg-blue-100 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-900">{stats.new}</div>
              <div className="text-sm text-blue-700">Nouveaux</div>
            </div>
            <div className="bg-yellow-100 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-900">{stats.contacted}</div>
              <div className="text-sm text-yellow-700">Contactés</div>
            </div>
            <div className="bg-green-100 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-900">{stats.accepted}</div>
              <div className="text-sm text-green-700">Acceptés</div>
            </div>
            <div className="bg-red-100 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-900">{stats.rejected}</div>
              <div className="text-sm text-red-700">Refusés</div>
            </div>
          </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setSelectedStatus('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedStatus === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Tous ({stats.total})
        </button>
        <button
          onClick={() => setSelectedStatus('new')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedStatus === 'new'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Nouveaux ({stats.new})
        </button>
        <button
          onClick={() => setSelectedStatus('contacted')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedStatus === 'contacted'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Contactés ({stats.contacted})
        </button>
        <button
          onClick={() => setSelectedStatus('accepted')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedStatus === 'accepted'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Acceptés ({stats.accepted})
        </button>
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Candidat
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Entreprise
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Objectif
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {filteredApplications.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                  Aucune candidature
                </td>
              </tr>
            ) : (
              filteredApplications.map((app) => (
                <tr key={app.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {new Date(app.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">
                      {app.firstname} {app.lastname}
                    </div>
                    <div className="text-sm text-slate-500">{app.email}</div>
                    {app.phone && <div className="text-sm text-slate-500">{app.phone}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {app.company_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {getCompanyTypeLabel(app.company_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {getMainGoalLabel(app.main_goal)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(app.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => openEditModal(app)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Voir / Modifier"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    {app.status === 'accepted' && app.user_id && (
                      <button
                        onClick={() => resendWelcomeEmail(app.user_id!, app.email, `${app.firstname} ${app.lastname}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Renvoyer l'email de bienvenue"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    )}
                    {app.status === 'new' && !app.user_id && (
                      <>
                        <button
                          onClick={() => updateStatus(app.id, 'contacted')}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Marquer comme contacté"
                        >
                          <Clock className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateStatus(app.id, 'accepted')}
                          className="text-green-600 hover:text-green-900"
                          title="Accepter et créer le compte"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateStatus(app.id, 'rejected')}
                          className="text-red-600 hover:text-red-900"
                          title="Refuser"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {selectedApp && isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Modifier la candidature</h3>
              <button
                onClick={() => {
                  setSelectedApp(null);
                  setIsEditing(false);
                  setEditForm({});
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={editForm.firstname || ''}
                    onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={editForm.lastname || ''}
                    onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Entreprise / Association *</label>
                <input
                  type="text"
                  value={editForm.company_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <select
                    value={editForm.company_type || ''}
                    onChange={(e) => setEditForm({ ...editForm, company_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="entreprise">Entreprise</option>
                    <option value="independant">Indépendant</option>
                    <option value="association">Association</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de salariés</label>
                  <input
                    type="text"
                    value={editForm.employees_range || ''}
                    onChange={(e) => setEditForm({ ...editForm, employees_range: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Objectif principal *</label>
                <select
                  value={editForm.main_goal || ''}
                  onChange={(e) => setEditForm({ ...editForm, main_goal: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="suivre">Suivre la trésorerie</option>
                  <option value="anticiper">Anticiper</option>
                  <option value="securiser">Sécuriser</option>
                  <option value="scenarios">Scénarios</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message du candidat</label>
                <textarea
                  rows={3}
                  value={editForm.message || ''}
                  onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Statut *</label>
                <select
                  value={editForm.status || ''}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as BetaApplication['status'] })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="new">Nouveau</option>
                  <option value="contacted">Contacté</option>
                  <option value="accepted">Accepté</option>
                  <option value="rejected">Refusé</option>
                </select>
                {selectedApp.user_id && editForm.status === 'accepted' && (
                  <p className="mt-1 text-xs text-green-600">
                    Un compte a déjà été créé pour ce candidat
                  </p>
                )}
                {!selectedApp.user_id && editForm.status === 'accepted' && selectedApp.status !== 'accepted' && (
                  <p className="mt-1 text-xs text-amber-600">
                    ⚠️ En changeant le statut à "Accepté", un compte sera créé et un email de bienvenue sera envoyé
                  </p>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes administratives</label>
                <textarea
                  rows={4}
                  value={editForm.admin_notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                  placeholder="Ajoutez vos notes internes ici..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="text-xs text-slate-500 space-y-1">
                <div>Date de candidature: {new Date(selectedApp.created_at).toLocaleString('fr-FR')}</div>
                {selectedApp.updated_at && selectedApp.updated_at !== selectedApp.created_at && (
                  <div>Dernière modification: {new Date(selectedApp.updated_at).toLocaleString('fr-FR')}</div>
                )}
                <div className="mt-2">
                  Consentement de contact: {' '}
                  {selectedApp.consent_contact ? (
                    <span className="text-green-600 font-medium">✓ Autorisé</span>
                  ) : (
                    <span className="text-red-600 font-medium">✗ Non autorisé</span>
                  )}
                </div>
                {selectedApp.user_id && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-800">
                    <strong>✓ Compte créé</strong> - User ID: {selectedApp.user_id}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setSelectedApp(null);
                    setIsEditing(false);
                    setEditForm({});
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                  disabled={isSaving}
                >
                  Annuler
                </button>
                <button
                  onClick={saveApplication}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Create Beta Founder Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Créer un Beta Founder</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateForm({
                    email: '',
                    firstName: '',
                    lastName: '',
                    company: '',
                    phone: ''
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Entreprise / Association</label>
                <input
                  type="text"
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Informations:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4 list-disc">
                  <li>Un compte avec accès Beta Founder sera créé</li>
                  <li>Un mot de passe temporaire sera généré automatiquement</li>
                  <li>Un email de bienvenue sera envoyé avec les identifiants</li>
                  <li>La période d'essai sera de 150 jours (5 mois)</li>
                  <li>Tous les modules premium seront activés</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({
                      email: '',
                      firstName: '',
                      lastName: '',
                      company: '',
                      phone: ''
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                  disabled={isCreating}
                >
                  Annuler
                </button>
                <button
                  onClick={createBetaFounderManually}
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-400 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Créer le compte
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
